import type { ReactNode } from "react";
import { getUnitCategoryName } from "verdi-common/i18n";
import { useSupportedLocale } from "@/hooks/use-locale";
import { ICON_KEYS, iconRegistry } from "@/icons/icon-registry";

const DeviceIcon = iconRegistry[ICON_KEYS.DEVICE];

/**
 * Up to four names — metric, device, sensor, channel — with the first leading and the rest smaller
 * and dimmer. Shared so the metric badge and the legend read the same, since which of them lands
 * where depends on the panel (see planPanelChrome).
 */
export function SeriesLabel({
    unitCategory,
    showUnitCategory = true,
    deviceName,
    sensorClusterName = null,
    sensorElementName = null,
}: {
    unitCategory: string | null;
    /** Off where the panel badge already names the metric. The name still counts against the channel below. */
    showUnitCategory?: boolean;
    deviceName: string | null;
    sensorClusterName?: string | null;
    sensorElementName?: string | null;
}) {
    const locale = useSupportedLocale();

    const categoryName = unitCategory ? getUnitCategoryName(unitCategory, locale) : null;

    // Most channels are named after their metric, so showing both reads "Temperature · Temperature".
    const elementName = sensorElementName === categoryName ? null : sensorElementName;

    // inline-flex, not flex: a block would take a line of its own instead of wrapping with the text
    // beside it.
    const device = deviceName ? (
        <span className={"inline-flex items-center gap-1.5 align-center"}>
            {deviceName}
            {/* Sized by class, not the size prop: Button's svg rule beats the attribute in the badge. */}
            <DeviceIcon className={"size-[10px]"} />
        </span>
    ) : null;

    const parts: { key: string; node: ReactNode }[] = [
        { key: "category", node: showUnitCategory ? categoryName : null },
        { key: "device", node: device },
        { key: "sensorCluster", node: sensorClusterName },
        { key: "sensorElement", node: elementName },
    ].filter((part) => part.node !== null);

    // Whichever name we have leads. The rest only shrink because they are following it.
    const [leading, ...trailing] = parts;
    if (!leading) return null;

    return (
        <>
            <span className={"text-[12px] font-normal leading-none"}>{leading.node}</span>
            {trailing.map((part) => (
                <span key={part.key} className={"text-[11px] font-normal text-muted-foreground/80 leading-none"}>
                    {/* nbsp after the dot: when the badge wraps, the separator goes with the name
                        rather than dangling at the end of the first line. */}
                    {" ·\u00a0"}
                    {part.node}
                </span>
            ))}
        </>
    );
}
